import React from 'react';
import axios from 'axios';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, ReferenceLine, Tooltip, ResponsiveContainer } from 'recharts';
import { T, MONO, tint } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { useIsMobile } from './poyais-ui/hooks';
import { exportCsv } from './poyais-ui/export';
import { useLang, addTranslations } from './i18n';
import { GrowthStyles, GrowthHero, SectionCard, SkeletonPage, ErrorState, cardStyle, tooltipStyle } from './GrowthUI';

addTranslations({
  'Büyüme': 'Growth',
  'Hangi ürün büyütülmeli, hangisi iyileştirilmeli?': 'Which products to scale, which to fix?',
  'Son 30 günde en az 10 kez görüntülenen ürünleri dönüşüm oranı ve görüntüleme başına gelire göre dört gruba ayırır.': 'Groups products viewed at least 10 times in the last 30 days into four segments by conversion rate and revenue per view.',
  'Performans Matrisi': 'Performance Matrix',
  'ürün, son 30 gün': 'products, last 30 days',
  'Bir gruba dokunarak grafikte öne çıkar.': 'Tap a segment to highlight it on the chart.',
  'Yıldızlar': 'Stars',
  'Nakit İnekleri': 'Cash Cows',
  'Soru İşaretleri': 'Question Marks',
  'Köpekler': 'Dogs',
  'Koru Ve Büyüt': 'Protect and scale',
  'Üst Satış / Paket': 'Upsell / bundle',
  'Dönüşümü İyileştir': 'Improve conversion',
  'Gözden Geçir': 'Review',
  'Dönüşüm ve görüntüleme başına gelir, ikisi de medyanın üzerinde. Koru ve büyüt.': 'Both conversion rate and revenue per view are above median. Protect and scale.',
  'Dönüşüm oranı yüksek ama görüntüleme başına gelir düşük, genelde düşük fiyatlı ürünler. Üst satış/paket fırsatı.': 'Converts well but earns little per view — typically low-ticket. Upsell and bundle.',
  'Görüntüleme başına gelir yüksek ama dönüşüm düşük, pahalı, bakılan ama az satılan ürünler. CRO hedefi.': 'High revenue per view, low conversion, expensive items that get looked at but rarely bought. A CRO target.',
  'Her iki metrikte de medyanın altında.': 'Below median on both metrics.',
  'ürün': 'products',
  'gelirin': 'of revenue',
  'Eşikler': 'Thresholds',
  'Ürün': 'Product',
  'Gelir': 'Revenue',
  'Dönüşüm': 'Conv.',
  'Dönüşüm oranı': 'Conversion rate',
  'Gör. Başına Gelir': 'Rev./view',
  'Görüntüleme Başına Gelir': 'Revenue per view',
  'Görüntüleme': 'Views',
  'Bu grupta ürün yok.': 'No products in this segment.',
  'Ürün verisi yüklenemedi.': 'Could not load product data.',
  'Daha Göster': 'more',
  'Dışa Aktar': 'Export',
  'Tümünü Göster': 'Show all',
  'Vurguyu kaldır': 'Clear highlight',
  'Yüksek dönüşüm': 'High conversion',
  'Yüksek gelir/görüntüleme': 'High revenue/view',
});

const API = '/api/v1';

type Product = {
  name: string;
  revenue: number;
  views: number;
  purchased: number;
  cr: number;
  rpv: number;
  quadrant: string;
  image_url?: string | null;
  product_url?: string | null;
};

type QuadrantGroup = { count: number; revenue: number; products: Product[] };
type ScatterPoint = { name: string; rpv: number; cr: number; revenue: number; quadrant: string };

type ApiResult = {
  performance: {
    thresholds: { cr: number; rpv: number; adjusted?: boolean; min_per_group?: number }; total_products: number;
    quadrants: Record<string, QuadrantGroup>; scatter: ScatterPoint[];
  };
};

const money = (v: number) => '₺' + Math.round(v || 0).toLocaleString('tr-TR');

// Sabit sıra + renk — kategorik kimlik hep aynı renkle eşleşsin (bkz. dataviz kuralı:
// "assign categorical hues in fixed order, never cycled").
const QUADRANTS: { id: string; label: string; action: string; desc: string; color: string; icon: (s: number) => React.ReactNode }[] = [
  { id: 'stars',          label: 'Yıldızlar',       action: 'Koru Ve Büyüt',      color: T.green,    icon: s => <Icon.sparkle size={s} sw={2} />,
    desc: 'Dönüşüm ve görüntüleme başına gelir, ikisi de medyanın üzerinde. Koru ve büyüt.' },
  { id: 'cash_cows',      label: 'Nakit İnekleri',  action: 'Üst Satış / Paket',  color: T.blue,     icon: s => <Icon.layers size={s} sw={2} />,
    desc: 'Dönüşüm oranı yüksek ama görüntüleme başına gelir düşük, genelde düşük fiyatlı ürünler. Üst satış/paket fırsatı.' },
  { id: 'question_marks', label: 'Soru İşaretleri', action: 'Dönüşümü İyileştir', color: T.amber,    icon: s => <Icon.target size={s} />,
    desc: 'Görüntüleme başına gelir yüksek ama dönüşüm düşük, pahalı, bakılan ama az satılan ürünler. CRO hedefi.' },
  { id: 'dogs',           label: 'Köpekler',        action: 'Gözden Geçir',       color: T.textDim,  icon: s => <Icon.warningTriangle size={s} sw={2} />,
    desc: 'Her iki metrikte de medyanın altında.' },
];
const Q_BY_ID = Object.fromEntries(QUADRANTS.map(q => [q.id, q]));
const textColor = (id: string) => id === 'stars' ? T.greenText : id === 'cash_cows' ? T.blueText : id === 'question_marks' ? T.amberDeepText : T.textMid;

function ChartTooltip({ active, payload }: any) {
  const { t } = useLang();
  if (!active || !payload?.length) return null;
  const p: Product = payload[0].payload;
  const q = Q_BY_ID[p.quadrant];
  return (
    <div style={tooltipStyle}>
      <div style={{ fontWeight: 800, color: T.text, marginBottom: 4, whiteSpace: 'normal', lineHeight: 1.35 }}>{p.name}</div>
      {q && <div style={{ color: textColor(q.id), fontWeight: 800, marginBottom: 4, fontSize: 11.5 }}>{t(q.label)}</div>}
      <div style={{ color: T.textMid }}>{t('Gelir')}: <b>{money(p.revenue)}</b></div>
      <div style={{ color: T.textMid }}>{t('Dönüşüm')}: <b>%{p.cr.toFixed(2)}</b></div>
      <div style={{ color: T.textMid }}>{t('Gör. Başına Gelir')}: <b>{money(p.rpv)}</b></div>
    </div>
  );
}

function Thumb({ src }: { src?: string | null }) {
  const [broken, setBroken] = React.useState(false);
  if (src && !broken) return <img src={src} alt="" width={36} height={36} onError={() => setBroken(true)} style={{ borderRadius: 9, objectFit: 'cover', flexShrink: 0, border: `1px solid ${T.border}` }} />;
  return null;
}

function ProductRow({ p, rank, maxRevenue, color }: { p: Product; rank: number; maxRevenue: number; color: string }) {
  const { t } = useLang();
  const nameEl = (
    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: p.product_url ? T.text : T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.name}>{p.name}</span>
  );
  return (
    <div className="gr-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', borderRadius: 10 }}>
      <span style={{ width: 20, fontSize: 11, fontWeight: 800, color: T.textFaint, textAlign: 'right', flexShrink: 0, fontFamily: MONO }}>{rank}</span>
      {/* Ürün görseli/link SADECE Shopify bağlıysa gelir (GA4'te görsel verisi yok) -
          yoksa sadece düz metin ürün adı gösterilir, kırık görsel/ölü link olmaz. */}
      <Thumb src={p.image_url} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
          {p.product_url ? (
            <a href={p.product_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', minWidth: 0, overflow: 'hidden', color: T.text }}>{nameEl}</a>
          ) : nameEl}
          {p.product_url && <span style={{ color: T.textFaint, display: 'flex', flexShrink: 0 }}><Icon.external size={11} /></span>}
        </div>
        <div style={{ height: 4, background: T.trackBg, borderRadius: 999, overflow: 'hidden', margin: '6px 0 5px' }}>
          <div style={{ width: `${maxRevenue > 0 ? Math.max(3, p.revenue / maxRevenue * 100) : 0}%`, height: '100%', background: color, opacity: 0.7, borderRadius: 999 }} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', fontSize: 11, color: T.textDim }}>
          <span>{t('Dönüşüm')} <b style={{ color: T.textMid, fontFamily: MONO }}>%{p.cr.toFixed(2)}</b></span>
          <span>{t('Gör. Başına Gelir')} <b style={{ color: T.textMid, fontFamily: MONO }}>{money(p.rpv)}</b></span>
          <span>{t('Görüntüleme')} <b style={{ color: T.textMid, fontFamily: MONO }}>{p.views.toLocaleString('tr-TR')}</b></span>
        </div>
      </div>
      <span style={{ fontSize: 13, fontWeight: 800, color: T.text, fontFamily: MONO, flexShrink: 0, whiteSpace: 'nowrap' }}>{money(p.revenue)}</span>
    </div>
  );
}

function QuadrantSection({ meta, group, isMobile, highlighted }: { meta: typeof QUADRANTS[0]; group: QuadrantGroup | undefined; isMobile: boolean; highlighted: boolean }) {
  const { t } = useLang();
  const [visibleCount, setVisibleCount] = React.useState(5);
  const products = group?.products || [];
  // Liste bir kerede DOM'a basılmıyor, her tıklamada 5 tane daha ekleniyor
  // (backend zaten en fazla 20/çeyrek dönüyor).
  const visible = products.slice(0, visibleCount);
  const maxRevenue = Math.max(0, ...products.map(p => p.revenue));

  const handleExport = () => {
    exportCsv(`buyume-${meta.id}.csv`, products, [
      { key: 'name', label: t('Ürün') },
      { key: 'revenue', label: t('Gelir') },
      { key: 'cr', label: t('Dönüşüm') },
      { key: 'rpv', label: t('Gör. Başına Gelir') },
      { key: 'views', label: t('Görüntüleme') },
    ]);
  };

  return (
    <div id={`gr-q-${meta.id}`} style={{ ...cardStyle, padding: isMobile ? 14 : 18, scrollMarginTop: 84, transition: 'box-shadow .2s, border-color .2s',
      ...(highlighted ? { borderColor: tint(meta.color, 55), boxShadow: `0 0 0 3px ${tint(meta.color, 18)}` } : {}) }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <span style={{ width: 38, height: 38, borderRadius: 12, background: tint(meta.color, 15), color: textColor(meta.id), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{meta.icon(18)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{t(meta.label)}</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: textColor(meta.id), background: tint(meta.color, 14), borderRadius: 999, padding: '2px 9px' }}>{t(meta.action)}</span>
          </div>
          <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.5, marginTop: 4 }}>{t(meta.desc)}</div>
        </div>
        {products.length > 0 && (
          <button onClick={handleExport} title={t('Dışa Aktar')}
            style={{ width: 32, height: 32, borderRadius: 9, border: `1px solid ${T.borderMid}`, background: T.surface, color: T.textMid, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon.download size={14} />
          </button>
        )}
      </div>

      {products.length === 0 ? (
        <div style={{ padding: '18px 0 6px', fontSize: 12.5, color: T.textDim, textAlign: 'center' }}>{t('Bu grupta ürün yok.')}</div>
      ) : (
        <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 6 }}>
          {visible.map((p, i) => <ProductRow key={i} p={p} rank={i + 1} maxRevenue={maxRevenue} color={meta.color} />)}
          {products.length > visibleCount && (
            <button onClick={() => setVisibleCount(c => c + 5)}
              style={{ marginTop: 6, width: '100%', padding: '9px', borderRadius: 10, border: `1px dashed ${T.borderMid}`, background: 'transparent', color: T.textMid,
                fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              +{Math.min(5, products.length - visibleCount)} {t('Daha Göster')} <span style={{ color: T.textDim, fontWeight: 600 }}>({products.length - visibleCount})</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function GrowthProductAnalysis({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [data, setData] = React.useState<ApiResult | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [focus, setFocus] = React.useState<string | null>(null);

  React.useEffect(() => {
    setLoading(true);
    setError(null);
    axios.get(`${API}/growth/product-analysis?client_id=${clientId}&days=30`)
      .then(res => setData(res.data))
      .catch(err => setError(err?.response?.data?.detail || t('Ürün verisi yüklenemedi.')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const quadrants = data?.performance.quadrants || {};
  const thresholds = data?.performance.thresholds;
  const totalRevenue = QUADRANTS.reduce((s, q) => s + (quadrants[q.id]?.revenue || 0), 0);
  const scatterByQuadrant: Record<string, ScatterPoint[]> = {};
  for (const p of data?.performance.scatter || []) {
    (scatterByQuadrant[p.quadrant] ||= []).push(p);
  }

  const toggleFocus = (id: string) => setFocus(f => f === id ? null : id);

  return (
    <div>
      <GrowthStyles />
      <GrowthHero isMobile={isMobile} color={T.green} icon={<Icon.grid size={22} sw={2.1} />}
        title={t('Hangi ürün büyütülmeli, hangisi iyileştirilmeli?')}
        desc={t('Son 30 günde en az 10 kez görüntülenen ürünleri dönüşüm oranı ve görüntüleme başına gelire göre dört gruba ayırır.')} />

      {loading && <SkeletonPage isMobile={isMobile} />}
      {!loading && error && <ErrorState error={error} />}

      {!loading && !error && data && (
        <div className="gr-fade">
          {/* Dört grup özeti — dokununca grafikte o grup öne çıkar */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0,1fr))' : 'repeat(4, minmax(0,1fr))', gap: isMobile ? 10 : 12, marginBottom: 14 }}>
            {QUADRANTS.map(q => {
              const g = quadrants[q.id];
              const share = totalRevenue > 0 ? (g?.revenue || 0) / totalRevenue * 100 : 0;
              const on = focus === q.id;
              return (
                <button key={q.id} onClick={() => toggleFocus(q.id)} className="gr-hover" style={{
                  ...cardStyle, padding: isMobile ? 12 : '14px 16px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', minWidth: 0,
                  display: 'flex', flexDirection: 'column', gap: 8,
                  ...(on ? { borderColor: tint(q.color, 60), background: tint(q.color, 8), boxShadow: `0 0 0 3px ${tint(q.color, 16)}` } : {}),
                  opacity: focus && !on ? 0.6 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 28, height: 28, borderRadius: 9, background: tint(q.color, 16), color: textColor(q.id), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{q.icon(14)}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t(q.label)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: T.text, fontFamily: MONO, letterSpacing: '-0.02em' }}>{g?.count || 0}</span>
                    <span style={{ fontSize: 11.5, color: T.textDim, fontWeight: 600 }}>{t('ürün')}</span>
                  </div>
                  <div>
                    <div style={{ height: 5, background: T.trackBg, borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ width: `${share}%`, height: '100%', background: q.color, borderRadius: 999 }} />
                    </div>
                    <div style={{ fontSize: 11, color: T.textDim, marginTop: 5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <b style={{ color: T.textMid, fontFamily: MONO }}>%{share.toFixed(0)}</b> {t('gelirin')} · {money(g?.revenue || 0)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <SectionCard style={{ marginBottom: 14 }} pad={isMobile ? 14 : 18}
            title={t('Performans Matrisi')}
            sub={<>{data.performance.total_products} {t('ürün, son 30 gün')} · {t('Bir gruba dokunarak grafikte öne çıkar.')}</>}
            right={focus ? (
              <button onClick={() => setFocus(null)} style={{ padding: '6px 12px', borderRadius: 999, border: `1px solid ${T.borderMid}`, background: T.surface, color: T.textMid,
                fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{t('Vurguyu kaldır')}</button>
            ) : undefined}>
            <div style={{ position: 'relative' }}>
              {/* Köşe etiketleri: hangi bölgenin hangi grup olduğu grafiğe bakınca anlaşılsın */}
              {!isMobile && (
                <>
                  <span style={{ position: 'absolute', right: 14, top: 4, fontSize: 11, fontWeight: 800, color: T.greenText, opacity: 0.8, pointerEvents: 'none' }}>{t('Yıldızlar')} ↗</span>
                  <span style={{ position: 'absolute', left: 60, top: 4, fontSize: 11, fontWeight: 800, color: T.blueText, opacity: 0.8, pointerEvents: 'none' }}>↖ {t('Nakit İnekleri')}</span>
                  <span style={{ position: 'absolute', right: 14, bottom: 34, fontSize: 11, fontWeight: 800, color: T.amberDeepText, opacity: 0.8, pointerEvents: 'none' }}>{t('Soru İşaretleri')} ↘</span>
                  <span style={{ position: 'absolute', left: 60, bottom: 34, fontSize: 11, fontWeight: 800, color: T.textDim, opacity: 0.8, pointerEvents: 'none' }}>↙ {t('Köpekler')}</span>
                </>
              )}
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 420}>
                <ScatterChart margin={{ top: 22, right: isMobile ? 6 : 16, bottom: 4, left: isMobile ? -12 : 0 }}>
                  {/* Log ölçek — gelir/görüntüleme oranı gibi metrikler doğası gereği çok
                      çarpık dağılır (birkaç ürün ortalamanın onlarca katı); doğrusal eksende
                      neredeyse tüm noktalar sol-alt köşede üst üste biner. */}
                  <XAxis type="number" dataKey="rpv" scale="log" domain={['auto', 'auto']} allowDataOverflow
                    name={t('Gör. Başına Gelir')} tick={{ fontSize: 10.5, fill: T.textDim as any }}
                    tickFormatter={(v) => money(v)} axisLine={{ stroke: T.border as any }} tickLine={false}
                    label={isMobile ? undefined : { value: t('Görüntüleme Başına Gelir') + ' →', position: 'insideBottomRight', offset: -2, fontSize: 10.5, fill: T.textDim as any }} />
                  <YAxis type="number" dataKey="cr" scale="log" domain={['auto', 'auto']} allowDataOverflow
                    name={t('Dönüşüm')} tick={{ fontSize: 10.5, fill: T.textDim as any }}
                    tickFormatter={(v) => `%${v}`} axisLine={{ stroke: T.border as any }} tickLine={false} width={isMobile ? 38 : 48} />
                  <ZAxis type="number" dataKey="revenue" range={[28, 320]} />
                  {thresholds && <ReferenceLine x={thresholds.rpv} stroke={T.borderMid as any} strokeDasharray="4 4" />}
                  {thresholds && <ReferenceLine y={thresholds.cr} stroke={T.borderMid as any} strokeDasharray="4 4" />}
                  <Tooltip content={<ChartTooltip />} cursor={{ strokeDasharray: '3 3' }} />
                  {QUADRANTS.map(q => (
                    <Scatter key={q.id} data={scatterByQuadrant[q.id] || []} fill={q.color as any} isAnimationActive={false}
                      fillOpacity={focus && focus !== q.id ? 0.12 : 0.75} stroke={focus === q.id ? (q.color as any) : 'none'}
                      onClick={() => toggleFocus(q.id)} style={{ cursor: 'pointer' }} />
                  ))}
                </ScatterChart>
              </ResponsiveContainer>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px', paddingTop: 8, borderTop: `1px solid ${T.divider}`, marginTop: 6 }}>
              {QUADRANTS.map(q => (
                <button key={q.id} onClick={() => toggleFocus(q.id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, color: focus && focus !== q.id ? T.textFaint : T.textMid, padding: 0 }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: q.color, display: 'inline-block', opacity: focus && focus !== q.id ? 0.35 : 1 }} />
                  {t(q.label)}
                </button>
              ))}
              {thresholds && (
                <span style={{ marginLeft: isMobile ? 0 : 'auto', fontSize: 11, color: T.textDim, fontWeight: 600 }}>
                  {t('Eşikler')}: {t('Dönüşüm')} %{thresholds.cr.toFixed(2)} · {t('Gör. Başına Gelir')} {money(thresholds.rpv)}
                </span>
              )}
            </div>
          </SectionCard>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', gap: 12, alignItems: 'stretch' }}>
            {QUADRANTS.map(q => <QuadrantSection key={q.id} meta={q} group={quadrants[q.id]} isMobile={isMobile} highlighted={focus === q.id} />)}
          </div>
        </div>
      )}
    </div>
  );
}
